import axios from 'axios';
import config from 'config';

import { jwt } from 'utils/jwt';
import { parentOrgStorage, subOrgStorage, tokenStorage } from 'utils/storage';

type ServerError = {
  [key: string]: {
    [key: string]: {
      message: string;
    };
  };
};

type FieldsData = {
  name: string;
  errors: string[];
};

export const getFieldsDataFromServerError: (
  errors: ServerError
) => FieldsData[] = (errors: ServerError) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const error = Object.keys(errors).reduce((acc: any[], key: string) => {
    return [
      ...acc,
      { name: key, errors: [errors[key].message || errors[key].msg] }
    ];
  }, []);

  return error as FieldsData[];
};

/**
 * Path prefix every admin REST endpoint sits under. Centralised so a
 * BE-side rename (e.g. /api/v2/admin → /api/v3/admin) is a one-line
 * change instead of a 28-file sweep.
 */
export const ADMIN_PREFIX = '/api/v2';

const instance = axios.create({
  baseURL: config.base_url
});

instance.interceptors.request.use(
  request => {
    const token = tokenStorage.get();

    if (token && request.headers) {
      request.headers['Authorization'] = `Bearer ${token}`;
    }

    const parentOrg = parentOrgStorage.get();
    const subOrg = subOrgStorage.get();

    const organization = subOrg || parentOrg;

    if (organization && request.headers) {
      request.headers['organization'] = organization;
    }

    // Admin must never serve cached responses — a stale list after
    // create/update/delete misleads operators. Send no-cache request
    // directives so the browser bypasses disk + memory cache and
    // revalidates with origin on every call. BE allowlist for these
    // headers must include Cache-Control + Pragma.
    if (request.headers) {
      request.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      request.headers['Pragma'] = 'no-cache';
    }

    return request;
  },
  error => {
    return Promise.reject(error);
  }
);

// Endpoints that DON'T carry a valid access token — refresh-and-retry on
// a 401 from these is wrong (e.g. wrong-credentials sign-in returning 401
// must surface to the form, not silently kick off a refresh roundtrip).
const NO_REFRESH_PATHS = [
  '/auth/sign-in',
  '/auth/verify-otp',
  '/auth/refresh-token',
  '/auth/forgot-password',
  '/auth/reset-password'
];

const shouldSkipRefresh = (url?: string): boolean => {
  if (!url) return false;

  return NO_REFRESH_PATHS.some(path => url.includes(path));
};

instance.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !shouldSkipRefresh(originalRequest?.url)
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await jwt.refreshToken();

        if (newToken) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

          return instance(originalRequest);
        }

        return Promise.reject(error);
      } catch (refreshError) {
        // Handle failure (e.g., redirect to login)
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 422) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorData: any = error?.response?.data;

      return new Promise((_, reject) => {
        if (errorData) {
          if (errorData.errors) {
            const errors = getFieldsDataFromServerError(errorData.errors);

            return reject({ ...errorData, errors });
          }

          return reject({ ...errorData, status: error?.response.status });
        }

        return reject(error);
      });
    }

    return Promise.reject(error);
  }
);

export default instance;
